/**
 * Fuel energy-content conversion data — domain-agnostic.
 *
 * `convert` (and the like-dimension canonical conversion) can only convert
 * within a physical dimension. Converting a FUEL QUANTITY (a volume of gas, a
 * mass of steam) into ENERGY needs the fuel's heating value, which depends on
 * the resource type. The energy-content factors come from the upstream
 * reference implementation's custom energy units (Watt-hours per unit,
 * ÷1000 here for kWh).
 *
 * `canonical.ts` merges FUEL_ENERGY_KWH into its unit table as kWh-anchored
 * energy units, and `convertCanonical(v, from, to, resourceType)` applies the
 * REMAP so a fuel quantity resolves to energy (and back). BEDES-specific slot
 * parsing (`resolveBedesResourceType`) lives in the bedes layer, not here.
 */
/**
 * Fuel-energy unit name → kWh of energy content per unit. Derived from the
 * upstream reference implementation's energy multipliers (Wh) ÷ 1000, with two
 * upstream copy-paste/inversion defects corrected (`PropaneLs`, `WoodTonnes` — see the
 * inline notes). `CHWTonHs` is defined in canonical.ts (12,000 Btu) so it is
 * not repeated here.
 */
export const FUEL_ENERGY_KWH = {
    // Natural gas (volume)
    NGCfs: 0.3007034, // 1 ft³ (~1,026 Btu)
    NGCCfs: 30.07034, // 100 ft³ (utility CCF)
    NGKCfs: 300.7034, // 1,000 ft³
    NGMCfs: 300703.4, // 1e6 ft³
    NGm3s: 10.639800703, // 1 m³
    NGKm3s: 10639.800703, // 1,000 m³
    NGLs: 0.010639800703, // 1 L
    // Fuel Oil #1 (volume)
    OilNo1USGs: 40.738569754,
    OilNo1UKGs: 48.923505275,
    OilNo1Ls: 10.762016413,
    // Fuel Oil #2 (volume) — ~138,500 Btu/US gal
    OilNo2USGs: 40.445486518,
    OilNo2UKGs: 48.571512309,
    OilNo2Ls: 10.684642438,
    // Fuel Oil #4 (volume)
    OilNo4USGs: 42.790152403,
    OilNo4UKGs: 51.387162954,
    OilNo4Ls: 11.303927315,
    // Fuel Oil #5/#6 (volume)
    OilNo6USGs: 43.962485346,
    OilNo6UKGs: 52.795134818,
    OilNo6Ls: 11.613716295,
    // Diesel (volume) — same energy content as #2 oil upstream
    DieselUSGs: 40.445486518,
    DieselUKGs: 48.571512309,
    DieselLs: 10.684642438,
    // Kerosene (volume)
    KeroseneUSGs: 39.566236811,
    KeroseneUKGs: 47.515533411,
    KeroseneLs: 10.452227433,
    // Propane (volume)
    PropaneUSGs: 26.963657679,
    PropaneUKGs: 32.381008206,
    // Corrected from upstream, where the per-litre propane multiplier was a
    // copy-paste of the per-cubic-foot multiplier (737.397421) — the per-cubic-foot value,
    // ~9.66x too small per litre. Derived as USG factor / 3.785411784 L/gal.
    PropaneLs: 7.123044788,
    // District steam (mass) — ~1,194 Btu/lb
    SteamLbs: 0.349941383,
    SteamKGs: 0.77139,
    SteamKLbs: 349.941383,
    SteamMLbs: 349941.383,
    // District chilled water (CHWTonHs lives in canonical.ts; these are the gal/L variants)
    CHWUSGs: 0.010412,
    CHWUKGs: 0.0125039,
    CHWLs: 0.0027506,
    // Coal anthracite (mass)
    CoalATons: 7353.458382181,
    CoalATonnes: 8106.20017585,
    CoalALbs: 3.676729191,
    // Coal bituminous (mass)
    CoalBitTons: 7306.565064478,
    CoalBitTonnes: 8054.513481829,
    CoalBitLbs: 3.653282532,
    // Coke (mass)
    CokeTons: 7268.464243845,
    CokeTonnes: 8012.602579132,
    CokeLbs: 3.634232122,
    // Wood (mass / cords)
    WoodTons: 5123.094958968,
    // Corrected from upstream, where the per-tonne wood multiplier applied
    // the ton→tonne ratio backwards (a metric tonne, 1000 kg, is heavier than a
    // US short ton, 907.18474 kg). Derived as USTon factor * 1000 / 907.18474,
    // matching every other mass fuel (tonne/ton ≈ 1.1023).
    WoodTonnes: 5647.24552021,
    WoodCords: 18738.4599816,
};
/**
 * Per-resource remap: canonical *quantity* unit → fuel-energy unit that carries
 * the heating value. Mirrors each fuel's upstream symbol-remap arms. Keys
 * are the canonical unit names in canonical.ts's table.
 */
export const RESOURCE_UNIT_REMAP = {
    natural_gas: {
        CubicFeet: "NGCfs",
        CentaCubicFeet: "NGCCfs",
        KiloCubicFeet: "NGKCfs",
        MegaCubicFeet: "NGMCfs",
        CubicMeters: "NGm3s",
        KiloCubicMeters: "NGKm3s",
        Litres: "NGLs",
    },
    fuel_oil_1: { UsGallons: "OilNo1USGs", ImperialGallons: "OilNo1UKGs", Litres: "OilNo1Ls" },
    fuel_oil_2: { UsGallons: "OilNo2USGs", ImperialGallons: "OilNo2UKGs", Litres: "OilNo2Ls" },
    fuel_oil_4: { UsGallons: "OilNo4USGs", ImperialGallons: "OilNo4UKGs", Litres: "OilNo4Ls" },
    fuel_oil_6: { UsGallons: "OilNo6USGs", ImperialGallons: "OilNo6UKGs", Litres: "OilNo6Ls" },
    diesel: { UsGallons: "DieselUSGs", ImperialGallons: "DieselUKGs", Litres: "DieselLs" },
    kerosene: { UsGallons: "KeroseneUSGs", ImperialGallons: "KeroseneUKGs", Litres: "KeroseneLs" },
    propane: { UsGallons: "PropaneUSGs", ImperialGallons: "PropaneUKGs", Litres: "PropaneLs" },
    steam: {
        Pounds: "SteamLbs",
        Kilograms: "SteamKGs",
        Kilopounds: "SteamKLbs",
        Megapounds: "SteamMLbs",
    },
    chilled_water: {
        USTons: "CHWTonHs",
        UsGallons: "CHWUSGs",
        ImperialGallons: "CHWUKGs",
        Litres: "CHWLs",
    },
    coal_anthracite: { USTons: "CoalATons", Tonnes: "CoalATonnes", Pounds: "CoalALbs" },
    coal_bituminous: { USTons: "CoalBitTons", Tonnes: "CoalBitTonnes", Pounds: "CoalBitLbs" },
    coke: { USTons: "CokeTons", Tonnes: "CokeTonnes", Pounds: "CokeLbs" },
    wood: { USTons: "WoodTons", Tonnes: "WoodTonnes", Cords: "WoodCords" },
};
/**
 * Remap a canonical quantity unit to its fuel-energy unit for a resource, but
 * only when crossing into energy: remap `unit` iff it's a fuel quantity for the
 * resource AND the counterpart unit is on the energy dimension.
 * `counterpartIsEnergy` keeps pure volume→volume / mass→mass conversions
 * unaffected.
 */
export function remapFuelUnit(unit, resourceType, counterpartIsEnergy) {
    if (!counterpartIsEnergy)
        return unit;
    return RESOURCE_UNIT_REMAP[resourceType]?.[unit] ?? unit;
}
//# sourceMappingURL=fuel.js.map